/**
 * Mahano font pairings. Each pairing supplies a display font (headings + body
 * everywhere in the app via the `--studio-font` CSS variable) and, optionally,
 * a display accent font hint for future use. Google Fonts are lazy-injected on
 * demand so we don't ship every family in the initial HTML.
 */

export const FONT_PAIRINGS = [
  {
    id: "sora-inter",
    label: "Sora × Inter",
    description: "Mahano default — geometric headings with a humanist body.",
    stack: `"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
    // Already loaded via <link> in index.html — no fetch needed.
    google: null,
  },
  {
    id: "satoshi-inter",
    label: "Satoshi × Inter",
    description: "Geometric grotesk with humanist body.",
    stack: `"Satoshi", "Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
    google: null,
  },
  {
    id: "inter",
    label: "Inter",
    description: "One family, quiet and neutral.",
    stack: `"Inter", -apple-system, BlinkMacSystemFont, system-ui, sans-serif`,
    google: "family=Inter:wght@400;500;600;700",
  },
  {
    id: "space-grotesk",
    label: "Space Grotesk × Inter",
    description: "Editorial + engineered. Great for tech-first studios.",
    stack: `"Space Grotesk", "Inter", -apple-system, system-ui, sans-serif`,
    google: "family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600",
  },
  {
    id: "dm-sans",
    label: "DM Sans × DM Mono",
    description: "Rounded, calm, product-y.",
    stack: `"DM Sans", -apple-system, system-ui, sans-serif`,
    google: "family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500",
  },
  {
    id: "playfair-inter",
    label: "Playfair × Inter",
    description: "Elegant editorial serif for headings, Inter for body.",
    stack: `"Inter", -apple-system, system-ui, sans-serif`,
    google: "family=Playfair+Display:wght@500;600;700&family=Inter:wght@400;500;600",
  },
  {
    id: "ibm-plex",
    label: "IBM Plex Sans × Mono",
    description: "Precise, editorial, corporate-grade.",
    stack: `"IBM Plex Sans", -apple-system, system-ui, sans-serif`,
    google: "family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500",
  },
  {
    id: "manrope",
    label: "Manrope",
    description: "Modern sans, mid-weighted, friendly.",
    stack: `"Manrope", -apple-system, system-ui, sans-serif`,
    google: "family=Manrope:wght@400;500;600;700;800",
  },
  {
    id: "system",
    label: "System UI",
    description: "Uses the OS's native font. Fastest, most native feel.",
    stack: `-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif`,
    google: null,
  },
];

const _loaded = new Set(["sora-inter", "satoshi-inter"]); // defaults already loaded

function _injectGoogleFont(id, query) {
  if (_loaded.has(id)) return;
  const href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.setAttribute("data-halo-font", id);
  document.head.appendChild(link);
  _loaded.add(id);
}

export function applyFontPairing(id) {
  const p = FONT_PAIRINGS.find((x) => x.id === id) || FONT_PAIRINGS[0];
  if (p.google) _injectGoogleFont(p.id, p.google);
  document.documentElement.style.setProperty("--studio-font", p.stack);
  return p;
}

export function getFontPairing(id) {
  if (!id) return FONT_PAIRINGS[0];
  const direct = FONT_PAIRINGS.find((x) => x.id === id);
  if (direct) return direct;
  // Legacy label-only values ("Satoshi", "Inter", …) — best-effort match on label prefix
  const lower = String(id).toLowerCase();
  const byLabel = FONT_PAIRINGS.find((x) => x.label.toLowerCase().startsWith(lower));
  return byLabel || FONT_PAIRINGS[0];
}
